import React, { createContext, useEffect, useState } from 'react';
import { Environment } from 'src/Environment';
import { getBCAOffers } from '../services/bca-offers.service';
import { queue } from 'src/helpers/delay';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import { AdditionalFieldsValues } from 'src/containers/self-service/business-cash-advance/calculation/additional-fields-modal/additional-fields-modal';

export interface Offer {
  OfferId: string;
  Amount: number;
  TotalRepayment: number;
  ApproximateInstalment: number;
  DailyAffordability?: number;
  TotalRepayments?: number;
  Term: number;
  Rate: string;
  Fee: number;
}

export type TopUpOffer = {
  id: string;
  anonymisedMerchantId: string;
  term: number;
  amount: number;
  pricingBandId: number;
  offerType: string;
  remittance: number;
  dailyAffordability: number;
  isSent: boolean;
  dateSent: string | null;
  isProcessed: boolean;
  dateProcessed: string | null;
  dateCreated: string;
  outcome: null | string;
  dateExpiry: string | null;
  isValid: boolean;
};

export interface RequestOffer {
  offerDetails: {
    QualificationStatus: number;
    CurrentBalance: number;
    AvailableLimit: number;
    offer: Offer;
  };
  amountTermsMap: { [key: string]: number[] };
}

type DefaultValues = {
  offers: RequestOffer | null;
  chosenOffer: Offer | {};
  setChosenOffer: React.Dispatch<React.SetStateAction<Offer | {}>>;
  setOffers: React.Dispatch<React.SetStateAction<RequestOffer | null>>;
  selectedOffer: Offer;
  additionalFieldValues?: AdditionalFieldsValues;
  earlySettlementAmount: number;
  isBCATopUp: boolean;
  setIsBCATopUp: React.Dispatch<React.SetStateAction<boolean>>;
  setEarlySettlementAmount: React.Dispatch<React.SetStateAction<number>>;
  setSelectedOffer: React.Dispatch<React.SetStateAction<Offer>>;
  setAdditionalFieldValues: React.Dispatch<
    React.SetStateAction<AdditionalFieldsValues | undefined>
  >;
};

const defaultValues: DefaultValues = {
  offers: null,
  chosenOffer: {},
  setChosenOffer: () => {},
  selectedOffer: {
    OfferId: '',
    Amount: 0,
    TotalRepayment: 0,
    ApproximateInstalment: 0,
    Term: 0,
    Rate: '',
    Fee: 0,
  },
  additionalFieldValues: undefined,
  earlySettlementAmount: 0,
  isBCATopUp: false,
  setEarlySettlementAmount: () => {},
  setAdditionalFieldValues: () => {},
  setSelectedOffer: () => {},
  setOffers: () => {},
  setIsBCATopUp: () => {},
};

export const OfferContext = createContext(defaultValues);

export function OfferProvider({ children }) {
  const [offers, setOffers] = useState<RequestOffer | null>(null);
  const [earlySettlementAmount, setEarlySettlementAmount] = useState<number>(0);
  const [isBCATopUp, setIsBCATopUp] = useState<boolean>(false);
  const [additionalFieldValues, setAdditionalFieldValues] = useState<AdditionalFieldsValues>();
  const [selectedOffer, setSelectedOffer] = useState<Offer>({
    OfferId: '',
    Amount: 0,
    TotalRepayment: 0,
    ApproximateInstalment: 0,
    Term: 0,
    Rate: '',
    Fee: 0,
  });
  const [chosenOffer, setChosenOffer] = useState<Offer | {}>({});

  const getOfferOptionsAsync = async (abortController: AbortController) => {
    const result = await getBCAOffers(abortController);
    if (!result.ok) {
      toastMessageServiceSingleton.addError(result?.message, null, '', false, null, true);
      return;
    }
    if (result.ok && !abortController.signal.aborted) {
      setOffers(result.result);
      setIsBCATopUp(
        result.result.offerDetails.QualificationStatus === 1 &&
          result.result.offerDetails.CurrentBalance > 0,
      );
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    if (!Environment.current?.disabledFeatures?.bca) {
      queue.push(() => getOfferOptionsAsync(abortController));
    }
    return () => {
      abortController.abort();
    };
  }, []);

  return (
    <OfferContext.Provider
      value={{
        offers,
        chosenOffer,
        setChosenOffer,
        selectedOffer,
        setSelectedOffer,
        additionalFieldValues,
        setAdditionalFieldValues,
        earlySettlementAmount,
        setEarlySettlementAmount,
        setOffers,
        isBCATopUp,
        setIsBCATopUp,
      }}
    >
      {children}
    </OfferContext.Provider>
  );
}
